import { useEffect, useState, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  TrendingUp,
  Loader2,
  Search,
  X,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import {
  purchasingAPI,
  ForecastTableRow,
  ForecastDateBucket,
  ForecastFilterOptions,
} from '../../../lib/api';
import SearchableSelect from '../../ui/SearchableSelect';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { toast } from 'sonner';
import { Pagination } from '../../ui/pagination';
import { useIsCustomer, useIsSupplier } from '../../../hooks/usePortalMode';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useView } from '../../../contexts/ViewContext';
import StockStatusIndicator, { StockStatusLegend, StockStatus } from '../StockStatusIndicator';

// Record type colors
const RECORD_TYPE_COLORS: Record<number, string> = {
  1: 'bg-blue-50 text-blue-800',      // Forecast
  2: 'bg-green-50 text-green-800',    // Open PO
  3: 'bg-purple-50 text-purple-800 font-semibold',  // Total
};

const RECORD_TYPE_LABELS: Record<number, string> = {
  1: 'Forecast',
  2: 'Open PO',
  3: 'Total',
};

// Record type sort order: Open PO first, then Forecast, then Total
// This ensures Open PO lines are shown before Forecast lines for each part
const RECORD_TYPE_ORDER: Record<number, number> = {
  2: 0,  // Open PO first
  1: 1,  // Forecast second
  3: 2,  // Total last
};

// Extended row type with stock status
interface ForecastTableRowWithStock extends ForecastTableRow {
  stock_status?: StockStatus;
}

export default function ForecastTab() {
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();
  const { isFeatureEnabled } = useMenuSettings();
  // CR-5286: forward dashboard top-bar selection (view_level + group/
  // company/site) so the Forecasting tab scopes to the same entity the
  // user is viewing instead of returning every tenant's data.
  const { viewLevel, getViewQueryParams, viewKey } = useView();
  const showStockStatus = isFeatureEnabled('supplier_stock_levels');
  // Firm-window column belongs to the Part Schedule module — only shown
  // for tenants that have schedule lines turned on (DE). On PNA / others
  // it would just render '-' across every row.
  const showFirmDays = isFeatureEnabled('po_line_schedules');

  const [data, setData] = useState<ForecastTableRowWithStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [partsCount, setPartsCount] = useState(0);
  // Quick part-number search lives outside the Filters drawer so users
  // don't have to expand it for the most common lookup.
  const [quickSearch, setQuickSearch] = useState('');
  const quickSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [buckets, setBuckets] = useState<{
    weeks: ForecastDateBucket[];
    months: ForecastDateBucket[];
  }>({ weeks: [], months: [] });
  const [filterOptions, setFilterOptions] = useState<ForecastFilterOptions | null>(null);
  const [exporting, setExporting] = useState(false);

  // Drill-down modal state
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownData, setDrilldownData] = useState<Record<string, unknown>[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState('');

  // Snapshot refresh state — only meaningful for installs that have the
  // po_line_schedules feature flag on (DE). On other tenants the API
  // returns { skipped: true } and the button stays disabled.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<{
    enabled: boolean;
    last_refreshed_at: string | null;
    last_status?: string;
    last_row_count?: number;
  } | null>(null);

  const pageSize = 50;
  const tableRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Filter state - CR-3855: Added supplierGroup, commodityType, importDateFrom, importDateTo
  const [filters, setFilters] = useState({
    partNumber: '',
    commodityGroup: '',
    commodityType: '',
    supplierGroup: '',
    supplierId: '',
    customerId: '',
    recordType: '',
    importDateFrom: '',
    importDateTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    partNumber: '',
    commodityGroup: '',
    commodityType: '',
    supplierGroup: '',
    supplierId: '',
    customerId: '',
    recordType: '',
    importDateFrom: '',
    importDateTo: '',
  });

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Clean up the quick-search debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
    };
  }, []);

  // Fetch data when page, filters, stock status feature, or top-bar view
  // selection (CR-5286) changes. fetchData itself is intentionally omitted
  // from deps — we trigger explicitly off state changes.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, showStockStatus, isSupplier, viewKey]);

  const loadFilterOptions = async () => {
    try {
      const response = await purchasingAPI.getForecastFilterOptions();
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string | number | boolean> = {
        page: currentPage,
        page_size: pageSize,
        company_type: isSupplier ? 'supplier' : 'customer',
        view_level: viewLevel,
      };

      // CR-5286: Add the view-context picker params (group / company /
      // site) so the backend can scope the table to the user's selection.
      const viewParams = getViewQueryParams();
      if (viewParams['group'] !== undefined) params['group'] = viewParams['group'];
      if (viewParams['company'] !== undefined) params['company'] = viewParams['company'];
      if (viewParams['site'] !== undefined) params['site'] = viewParams['site'];

      if (appliedFilters.partNumber) {
        params['part_number'] = appliedFilters.partNumber;
      }
      if (appliedFilters.commodityGroup) {
        params['commodity_group'] = appliedFilters.commodityGroup;
      }
      // CR-3855: Additional filter parameters
      if (appliedFilters.commodityType) {
        params['commodity_type'] = appliedFilters.commodityType;
      }
      if (appliedFilters.supplierGroup) {
        params['supplier_group'] = appliedFilters.supplierGroup;
      }
      if (appliedFilters.supplierId) {
        params['supplier_id'] = parseInt(appliedFilters.supplierId);
      }
      if (appliedFilters.customerId) {
        params['customer_id'] = parseInt(appliedFilters.customerId);
      }
      if (appliedFilters.importDateFrom) {
        params['import_date_from'] = appliedFilters.importDateFrom;
      }
      if (appliedFilters.importDateTo) {
        params['import_date_to'] = appliedFilters.importDateTo;
      }
      if (appliedFilters.recordType) {
        params['record_type_filter'] = parseInt(appliedFilters.recordType);
      }

      // Include stock status if feature is enabled
      if (showStockStatus) {
        params['include_stock_status'] = true;
      }

      const response = await purchasingAPI.getForecastTableData(params);
      // CR-4727: Handle both 'results' and 'rows' keys from backend
      setData(response.data.results ?? response.data.rows ?? []);
      const rowCount = response.data.total_count ?? response.data.total ?? 0;
      setTotalCount(rowCount);
      setPartsCount(response.data.parts_count ?? rowCount);
      setTotalPages(Math.ceil(rowCount / pageSize));
      setBuckets(response.data.buckets || { weeks: [], months: [] });
    } catch (err: unknown) {
      console.error('Error fetching forecast data:', err);
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    // CR-3855: Clear all filter fields including new ones
    const empty = {
      partNumber: '',
      commodityGroup: '',
      commodityType: '',
      supplierGroup: '',
      supplierId: '',
      customerId: '',
      recordType: '',
      importDateFrom: '',
      importDateTo: '',
    };
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
  };

  // Load the refresh-status row so we can render the "Last updated" line
  // and decide whether to show the Refresh button (only for DE-style
  // installs with the snapshot enabled).
  const loadRefreshStatus = async () => {
    try {
      const resp = await purchasingAPI.getForecastRefreshStatus();
      setRefreshStatus(resp.data);
    } catch {
      setRefreshStatus(null);
    }
  };

  useEffect(() => {
    loadRefreshStatus();
  }, []);

  const handleRefreshSnapshot = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const resp = await purchasingAPI.refreshForecastSnapshot();
      if (resp.data?.skipped) {
        toast.info('Snapshot refresh is not enabled for this install.');
        setRefreshing(false);
        return;
      }
      toast.success('Refresh queued — fetching new data when ready.');
      // Poll the refresh-status endpoint until last_status flips to 'ok'.
      const startedAt = new Date(refreshStatus?.last_refreshed_at || 0).getTime();
      let attempts = 0;
      const poll = async (): Promise<void> => {
        attempts++;
        if (attempts > 60) {  // hard cap ≈ 2 minutes at 2s intervals
          setRefreshing(false);
          toast.error('Refresh is taking longer than expected — try again later.');
          return;
        }
        const statusResp = await purchasingAPI.getForecastRefreshStatus();
        const completedAt = new Date(statusResp.data?.last_refreshed_at || 0).getTime();
        if (statusResp.data?.last_status === 'ok' && completedAt > startedAt) {
          setRefreshStatus(statusResp.data);
          await fetchData();
          setRefreshing(false);
          toast.success('Forecast snapshot refreshed.');
          return;
        }
        if (statusResp.data?.last_status === 'failed') {
          setRefreshStatus(statusResp.data);
          setRefreshing(false);
          toast.error('Refresh failed — check server logs.');
          return;
        }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (err) {
      console.error('Forecast snapshot refresh failed:', err);
      toast.error('Failed to trigger refresh.');
      setRefreshing(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      // Mirror the table_data params so the CSV reflects the on-screen
      // filters (and the view-context picker — group/company/site).
      const viewParams = getViewQueryParams();
      const response = await purchasingAPI.exportForecasts({
        part_number: appliedFilters.partNumber || undefined,
        commodity_group: appliedFilters.commodityGroup || undefined,
        commodity_type: appliedFilters.commodityType || undefined,
        supplier_group: appliedFilters.supplierGroup || undefined,
        supplier_id: appliedFilters.supplierId ? parseInt(appliedFilters.supplierId) : undefined,
        customer_id: appliedFilters.customerId ? parseInt(appliedFilters.customerId) : undefined,
        record_type_filter: appliedFilters.recordType ? parseInt(appliedFilters.recordType) : undefined,
        import_date_from: appliedFilters.importDateFrom || undefined,
        import_date_to: appliedFilters.importDateTo || undefined,
        view_level: viewLevel,
        group: viewParams['group'] !== undefined ? Number(viewParams['group']) : undefined,
        company: viewParams['company'] !== undefined ? Number(viewParams['company']) : undefined,
        site: viewParams['site'] !== undefined ? Number(viewParams['site']) : undefined,
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forecast_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Forecast data exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export forecast data');
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Horizontal scroll handling
  const handleScroll = (direction: 'left' | 'right') => {
    if (tableRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left'
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      tableRef.current.scrollLeft = newPosition;
      setScrollPosition(newPosition);
    }
  };

  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollPosition(e.currentTarget.scrollLeft);
  };

  // Drill-down handler — fetch detail for a specific cell
  const handleCellClick = async (
    row: ForecastTableRowWithStock,
    bucketKey: string,
    bucketLabel: string
  ) => {
    const qty = (row as unknown as Record<string, string>)[bucketKey];
    if (!qty || parseFloat(qty) === 0) return;
    if (row.record_type === 3) return; // Total rows are calculated, not drillable

    const rowSupplier = (row as unknown as Record<string, unknown>)['supplier'] || '';
    const rowSupplierId = (row as unknown as Record<string, unknown>)['supplier_id'] as number | undefined;
    setDrilldownTitle(`${row.part_number} — ${rowSupplier || 'Unassigned'} — ${RECORD_TYPE_LABELS[row.record_type]} — ${bucketLabel}`);
    setDrilldownOpen(true);
    setDrilldownLoading(true);
    try {
      const response = await purchasingAPI.getForecastCellDetail({
        part_number: row.part_number,
        record_type: row.record_type as 1 | 2,
        date_bucket: bucketKey,
        customer_id: filters.customerId ? Number(filters.customerId) : undefined,
        supplier_id: rowSupplierId || (filters.supplierId ? Number(filters.supplierId) : undefined),
      });
      const data = response.data as Record<string, unknown>;
      setDrilldownData((data['results'] || data['rows'] || []) as Record<string, unknown>[]);
    } catch {
      toast.error('Failed to load detail data');
      setDrilldownData([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Format quantity for display
  const formatQty = (value: string | undefined) => {
    if (!value || value === '0' || value === '0.000') return '-';
    const num = parseFloat(value);
    if (num === 0) return '-';
    return num.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  };

  // Total rows (record_type 3) are hidden by default — admins can flip the
  // 'show_forecast_total' feature flag on via Admin Setup > Forecast.
  const showTotalRow = isFeatureEnabled('show_forecast_total');

  // Group rows by (part_number, supplier) so every distinct supplier for the
  // same part renders its own contiguous block (Open PO, Forecast[, Total])
  // instead of clustering all Open POs / Forecasts / Totals separately.
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const groupKey = (row: ForecastTableRowWithStock): string => {
      const supplier = (row as unknown as Record<string, string>)['supplier'] || '';
      return `${row.part_number}\u0000${supplier}`;
    };

    const groups: Map<string, ForecastTableRowWithStock[]> = new Map();
    const order: string[] = [];
    data.forEach(row => {
      if (!showTotalRow && row.record_type === 3) return;
      const key = groupKey(row);
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)!.push(row);
    });

    const result: ForecastTableRowWithStock[] = [];
    order.forEach(key => {
      const group = groups.get(key)!;
      group.sort((a, b) => RECORD_TYPE_ORDER[a.record_type] - RECORD_TYPE_ORDER[b.record_type]);
      result.push(...group);
    });
    return result;
  }, [data, showTotalRow]);

  // Build column headers from buckets
  const weekColumns = useMemo(() =>
    (buckets?.weeks || []).map(b => ({
      key: b.key,
      label: b.label,
      tooltip: `${b.start_date} to ${b.end_date}`,
    })),
    [buckets?.weeks]
  );

  const monthColumns = useMemo(() =>
    (buckets?.months || []).map(b => ({
      key: b.key,
      label: b.label,
      tooltip: `${b.start_date} to ${b.end_date}`,
    })),
    [buckets?.months]
  );

  if (loading && data.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading forecast data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              <CardTitle>Forecast Schedule</CardTitle>
              <span className="text-sm text-muted-foreground ml-2">
                ({partsCount.toLocaleString()} parts)
              </span>
            </div>
            {refreshStatus?.enabled && refreshStatus.last_refreshed_at && (
              <span className="text-[11px] text-muted-foreground ml-7 mt-0.5">
                Last refreshed {new Date(refreshStatus.last_refreshed_at).toLocaleString()}
                {refreshStatus.last_status === 'failed' && (
                  <span className="text-red-600 ml-2">· last refresh failed</span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Quick part-number search — debounced 400ms, applies independently of the Filters drawer. */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={quickSearch}
                placeholder="Quick search part..."
                onChange={(e) => {
                  const value = e.target.value;
                  setQuickSearch(value);
                  setFilters((prev) => ({ ...prev, partNumber: value }));
                  if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
                  quickSearchTimerRef.current = setTimeout(() => {
                    setAppliedFilters((prev) => ({ ...prev, partNumber: value }));
                    setCurrentPage(1);
                  }, 400);
                }}
                className="pl-8 pr-3 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-56"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-primary text-primary-foreground' : ''}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            {refreshStatus?.enabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshSnapshot}
                disabled={refreshing}
                title={refreshStatus?.last_refreshed_at
                  ? `Last refreshed ${new Date(refreshStatus.last_refreshed_at).toLocaleString()}`
                  : 'Trigger an on-demand snapshot refresh'}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters Panel - CR-3855: Added Supplier Group, Commodity Type, Import Date filters */}
        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Part Number
                </label>
                <input
                  type="text"
                  value={filters.partNumber}
                  onChange={(e) => setFilters({ ...filters, partNumber: e.target.value })}
                  placeholder="Search part..."
                  className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commodity Group
                </label>
                <select
                  value={filters.commodityGroup}
                  onChange={(e) => setFilters({ ...filters, commodityGroup: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Groups</option>
                  {(filterOptions?.commodity_groups || []).map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              </div>
              {/* CR-3855: Commodity Type filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commodity Type
                </label>
                <select
                  value={filters.commodityType}
                  onChange={(e) => setFilters({ ...filters, commodityType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Types</option>
                  {(filterOptions?.commodity_types || []).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              {/* CR-3855: Supplier Group filter */}
              {isCustomer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Group
                  </label>
                  <SearchableSelect
                    options={(filterOptions?.supplier_groups || []).map(g => ({ value: g.name, label: g.name }))}
                    value={filters.supplierGroup}
                    onChange={(val) => setFilters({ ...filters, supplierGroup: val })}
                    placeholder="All Supplier Groups"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {isCustomer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Site
                  </label>
                  <SearchableSelect
                    options={(filterOptions?.suppliers || []).map(s => ({ value: String(s.id), label: s.name }))}
                    value={filters.supplierId}
                    onChange={(val) => setFilters({ ...filters, supplierId: val })}
                    placeholder="All Supplier Sites"
                  />
                </div>
              )}
              {isSupplier && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <select
                    value={filters.customerId}
                    onChange={(e) => setFilters({ ...filters, customerId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Customers</option>
                    {(filterOptions?.customers || []).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* Record Type filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Record Type
                </label>
                <select
                  value={filters.recordType}
                  onChange={(e) => setFilters({ ...filters, recordType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary"
                >
                  <option value="">All</option>
                  <option value="2">Open PO</option>
                  <option value="1">Forecast</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={handleSearch} size="sm">
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
                <Button variant="outline" onClick={handleClearFilters} size="sm">
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowFilters(false)}>
                  <Filter className="h-4 w-4 mr-2" />
                  Hide Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Quick filter strip (Open PO vs Forecast) — count summary intentionally
            removed: the per-page "(N parts)" total in the title bar is the source of truth. */}
        {(() => {
          const segValue = appliedFilters.recordType;
          const setSeg = (val: string) => {
            const next = { ...filters, recordType: val };
            setFilters(next);
            setAppliedFilters({ ...appliedFilters, recordType: val });
            setCurrentPage(1);
          };
          return (
            <div className="flex items-center justify-end mb-3 p-3 rounded-md bg-slate-50 border">
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant={segValue === '' ? 'default' : 'outline'}
                  onClick={() => setSeg('')}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={segValue === '2' ? 'default' : 'outline'}
                  onClick={() => setSeg('2')}
                  className={segValue === '2' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  Open PO only
                </Button>
                <Button
                  size="sm"
                  variant={segValue === '1' ? 'default' : 'outline'}
                  onClick={() => setSeg('1')}
                  className={segValue === '1' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  Forecast only
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Horizontal Scroll Controls */}
        <div className="flex items-center justify-between mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleScroll('left')}
            disabled={scrollPosition === 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Scroll Left
          </Button>
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Weeks 1-12 | Months 1-24 | Future
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleScroll('right')}
          >
            Scroll Right
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Forecast Table */}
        <div
          ref={tableRef}
          onScroll={handleTableScroll}
          className="overflow-x-auto border rounded-lg"
          style={{ maxHeight: 'calc(100vh - 400px)' }}
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr>
                {/* CR-4726 round 2: column order per spec —
                    Customer | Supplier | Part Number | Part Description |
                    UOM | Type | Arrears | Weeks | Months | Future | Total.
                    Customer pinned sticky-left so horizontal scroll keeps
                    the row's owning customer visible at all times. */}
                <th className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap sticky left-0 bg-slate-100 z-20 min-w-[180px] max-w-[220px]">
                  Customer
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap min-w-[160px] max-w-[200px] bg-slate-100">
                  Supplier
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap min-w-[120px] bg-slate-100">
                  Part Number
                </th>
                {/* CR-3854: Part Description column */}
                <th className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap min-w-[180px] bg-slate-100">
                  Part Description
                </th>
                {/* CR-4726 round 2: UOM column added per spec. */}
                <th className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap min-w-[70px] bg-slate-100">
                  UOM
                </th>
                {showStockStatus && (
                  <th className="px-2 py-2 text-center font-medium text-slate-700 whitespace-nowrap min-w-[50px] bg-slate-100">
                    Stock
                  </th>
                )}
                <th className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap min-w-[100px]">
                  Type
                </th>
                {showFirmDays && (
                  <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[80px]" title="Firm boundary in days (PartSchedule / PartScheduleEntry schedule_firm). PO demand with delivery date within this window is Open PO; beyond is Forecast.">
                    Firm days
                  </th>
                )}
                <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[80px]">
                  Arrears
                </th>

                {/* Weekly columns */}
                {weekColumns.map(col => (
                  <th
                    key={col.key}
                    className="px-2 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[70px] bg-blue-50"
                    title={col.tooltip}
                  >
                    {col.label}
                  </th>
                ))}

                {/* Monthly columns */}
                {monthColumns.map(col => (
                  <th
                    key={col.key}
                    className="px-2 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[70px] bg-amber-50"
                    title={col.tooltip}
                  >
                    {col.label}
                  </th>
                ))}

                {/* Future and Total */}
                <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[80px] bg-slate-200">
                  Future
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[90px] bg-slate-200">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sortedData.length === 0 ? (
                <tr>
                  {/* CR-3854 + CR-4726: colspan includes Part Description, Customer, UOM. */}
                  <td colSpan={9 + weekColumns.length + monthColumns.length + 2 + (showStockStatus ? 1 : 0)} className="px-6 py-12 text-center text-slate-500">
                    No forecast data available. Import forecast data or check your filters.
                  </td>
                </tr>
              ) : (
                sortedData.map((row, idx) => (
                  <tr
                    key={`${row.part_number}-${row.record_type}-${idx}`}
                    className={`${RECORD_TYPE_COLORS[row.record_type]} hover:opacity-80 transition-opacity`}
                  >
                    {/* CR-4726 round 2: column order matches the header —
                        Customer (sticky), Supplier, Part Number, Part
                        Description, UOM, Stock?, Type, Firm days, Arrears.
                        Every row carries the full identifying set so
                        Forecast / Total rows for different parts stay
                        distinguishable when sorted together. */}
                    <td className="px-3 py-2 text-sm sticky left-0 z-10 min-w-[180px] max-w-[220px] break-words"
                        style={{ backgroundColor: 'inherit' }}>
                      {(row as unknown as Record<string, string>)['customer'] || '-'}
                    </td>
                    <td className="px-3 py-2 text-sm min-w-[160px] max-w-[200px] break-words"
                        style={{ backgroundColor: 'inherit' }}>
                      {(row as unknown as Record<string, string>)['supplier'] || 'Unassigned'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium"
                        style={{ backgroundColor: 'inherit' }}>
                      {row.part_number}
                    </td>
                    <td className="px-3 py-2 text-sm max-w-[220px]"
                        style={{ backgroundColor: 'inherit' }}>
                      {row.description || '-'}
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap"
                        style={{ backgroundColor: 'inherit' }}>
                      {(row as unknown as Record<string, string>)['uom'] || '-'}
                    </td>
                    {showStockStatus && (
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        {row.stock_status && (
                          <StockStatusIndicator stockStatus={row.stock_status} />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        row.record_type === 1 ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                        row.record_type === 2 ? 'bg-green-50 text-green-700 border border-green-200' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {row.record_type === 2 ? 'OPEN PO' : row.record_type === 1 ? 'FORECAST' : RECORD_TYPE_LABELS[row.record_type]}
                      </span>
                    </td>
                    {showFirmDays && (
                      <td className="px-3 py-2 text-right whitespace-nowrap font-mono text-xs">
                        {row.firm_days != null ? row.firm_days : '-'}
                      </td>
                    )}
                    <td
                      className={`px-3 py-2 text-right whitespace-nowrap font-mono text-xs ${row.record_type !== 3 && row.arrears && parseFloat(row.arrears) > 0 ? 'cursor-pointer hover:bg-red-50' : ''}`}
                      onClick={row.record_type !== 3 && row.arrears && parseFloat(row.arrears) > 0 ? () => handleCellClick(row, 'arrears', 'Arrears') : undefined}
                    >
                      {row.arrears && parseFloat(row.arrears) > 0 ? (
                        <span className="text-red-600 font-semibold underline decoration-dotted cursor-pointer">{formatQty(row.arrears)}</span>
                      ) : (
                        formatQty(row.arrears)
                      )}
                    </td>

                    {/* Weekly data */}
                    {weekColumns.map(col => {
                      const val = (row as unknown as Record<string, string>)[col.key];
                      const clickable = row.record_type !== 3 && val && parseFloat(val) !== 0;
                      return (
                        <td
                          key={col.key}
                          className={`px-2 py-2 text-right whitespace-nowrap font-mono text-xs bg-blue-50/30 ${clickable ? 'cursor-pointer hover:bg-blue-100' : ''}`}
                          onClick={clickable ? () => handleCellClick(row, col.key, col.label) : undefined}
                        >
                          {clickable ? (
                            <span className="text-primary underline decoration-dotted">{formatQty(val)}</span>
                          ) : formatQty(val)}
                        </td>
                      );
                    })}

                    {/* Monthly data */}
                    {monthColumns.map(col => {
                      const val = (row as unknown as Record<string, string>)[col.key];
                      const clickable = row.record_type !== 3 && val && parseFloat(val) !== 0;
                      return (
                        <td
                          key={col.key}
                          className={`px-2 py-2 text-right whitespace-nowrap font-mono text-xs bg-amber-50/30 ${clickable ? 'cursor-pointer hover:bg-amber-100' : ''}`}
                          onClick={clickable ? () => handleCellClick(row, col.key, col.label) : undefined}
                        >
                          {clickable ? (
                            <span className="text-primary underline decoration-dotted">{formatQty(val)}</span>
                          ) : formatQty(val)}
                        </td>
                      );
                    })}

                    {/* Future and Total */}
                    <td className="px-3 py-2 text-right whitespace-nowrap font-mono text-xs bg-slate-100">
                      {formatQty(row.future)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap font-mono text-xs font-semibold bg-slate-100">
                      {formatQty(row.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Legend - Order: Open PO first, then Forecast, then Total */}
        <div className="flex flex-col gap-2 mt-4">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
              <span>Open PO (within firm boundary)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200"></div>
              <span>Forecast (Schedule Firm boundary)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-purple-100 border border-purple-200"></div>
              <span>Total (Open PO + Forecast)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-red-600 font-semibold">Red</span>
              <span>= Arrears (past due)</span>
            </div>
          </div>
          {showStockStatus && <StockStatusLegend />}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              itemLabel="parts"
            />
          </div>
        )}
      </CardContent>
    </Card>

    {/* Drill-down Modal */}
    <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Forecast Detail</DialogTitle>
          <DialogDescription>{drilldownTitle}</DialogDescription>
        </DialogHeader>
        {drilldownLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : drilldownData.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No detail records found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">PO Number</th>
                  <th className="px-3 py-2 text-left font-medium">Line</th>
                  <th className="px-3 py-2 text-left font-medium">Part Number</th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {drilldownData.map((item: Record<string, unknown>, idx: number) => (
                  <tr key={idx} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item['type'] === 'forecast' ? 'bg-blue-100 text-blue-800' :
                        item['type'] === 'po_line_forecast' ? 'bg-purple-100 text-purple-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {item['type'] === 'forecast' ? 'Forecast' : item['type'] === 'po_line_forecast' ? 'PO (Forecast)' : 'Open PO'}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{String(item['po_number'] || '-')}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{String(item['line_number'] || '-')}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{String(item['part_number'] || '')}</td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">{String(item['description'] || '')}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{String(item['forecast_date'] || item['delivery_date'] || '')}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">
                      {parseFloat(String(item['qty_outstanding'] || item['qty_forecast'] || 0)).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
